import { useQuery } from '@tanstack/react-query';
import { BigNumber } from 'bignumber.js';

import { getWalletPortfolioOptions } from '@/api/portfolio/queries';

import { UserSuiWalletStatus } from './use-privy-wallet';
import { useUserSuiWallet } from './use-user-sui-wallet';

export interface CryptoAsset {
  positionId?: string;
  coinType: string;
  symbol: string;
  decimals: number;
  balance: BigNumber;
  price: number | null;
  usdValue: number | null;
  costBasisInUsd?: string | null;
  iconUrl: string | null;
}

const toOptionalNumber = (value: string | null): number | null => {
  if (value === null) return null;
  const parsed = new BigNumber(value);
  if (!parsed.isFinite()) throw new Error('Invalid wallet portfolio valuation');
  return parsed.toNumber();
};

const resolveUsdValue = (
  balance: BigNumber,
  currentPriceInUsd: string | null,
  positionValueInUsd: string | null | undefined,
): number | null => {
  if (positionValueInUsd !== null && positionValueInUsd !== undefined) {
    return toOptionalNumber(positionValueInUsd);
  }
  if (currentPriceInUsd === null) return null;
  return toOptionalNumber(balance.multipliedBy(currentPriceInUsd).toFixed());
};

export interface UseCryptoAssetsResult {
  assets: CryptoAsset[];
  isLoading: boolean;
  isWalletError: boolean;
  walletStatus: UserSuiWalletStatus;
  suiAddress: string | undefined;
}

export function useCryptoAssets({
  enabled = true,
}: { enabled?: boolean } = {}): UseCryptoAssetsResult {
  const wallet = useUserSuiWallet();
  const walletReady = wallet.status === UserSuiWalletStatus.READY;
  const portfolioQuery = useQuery({
    ...getWalletPortfolioOptions(),
    enabled: enabled && walletReady,
  });

  const positionsByAsset = new Map(
    (portfolioQuery.data?.positions ?? []).map((position) => [position.assetId, position]),
  );
  const assets: CryptoAsset[] = (portfolioQuery.data?.balances ?? [])
    .map((item) => {
      const rawBalance = new BigNumber(item.amount);
      if (!rawBalance.isFinite() || rawBalance.isNegative() || !rawBalance.isInteger()) {
        throw new Error(`Invalid portfolio balance for ${item.asset.coinType}`);
      }
      const balance = rawBalance.shiftedBy(-item.asset.decimals);
      const position = positionsByAsset.get(item.asset.id);
      return {
        positionId: position?.id,
        coinType: item.asset.coinType,
        symbol: item.asset.symbol,
        decimals: item.asset.decimals,
        balance,
        price: toOptionalNumber(item.currentPriceInUsd),
        usdValue: resolveUsdValue(balance, item.currentPriceInUsd, position?.currentValueInUsd),
        costBasisInUsd: position?.costBasisInUsd ?? null,
        iconUrl: item.asset.iconUrl,
      };
    })
    .sort((a, b) => (b.usdValue ?? -1) - (a.usdValue ?? -1));

  return {
    assets,
    isLoading:
      enabled &&
      (wallet.status === UserSuiWalletStatus.LOADING ||
        wallet.status === UserSuiWalletStatus.PROVISIONING ||
        (walletReady && portfolioQuery.isLoading)),
    isWalletError:
      enabled && (wallet.status === UserSuiWalletStatus.ERROR || portfolioQuery.isError),
    walletStatus: wallet.status,
    suiAddress: portfolioQuery.data?.wallet.address ?? wallet.address,
  };
}
